/**
 * Does a condition NAME an item — is `has_item <itemId>` anywhere in it?
 *
 * @remarks
 * A door's `handOpenCondition` says what hands need, and when it says
 * `has_item item_key` it has named the key as the thing that opens the door.
 * `use the key on the chest` is that door's opening, spoken from the key's
 * side, and the engine reads the declaration to know it (D194) rather than
 * asking the pack to author a second, mirrored fact.
 *
 * Structural, never evaluated: the question is whether the condition
 * mentions the item, not whether it holds — `canOpenByHand` answers the
 * second. A `not(has_item …)` still names the item (the door that refuses
 * you while you carry the thing is a door with an opinion about it), which
 * is why the walk does not stop at negation.
 *
 * @packageDocumentation
 */

import type { ConditionDefinition, ItemId } from '@orpheus/content-schema';

/**
 * Whether `condition` names `itemId` through a `has_item` leaf, at any depth.
 *
 * @param condition - The condition to read
 * @param itemId - The item asked after
 * @returns Whether the condition mentions it
 */
export const conditionNamesItem = (condition: ConditionDefinition, itemId: ItemId): boolean => {
	switch (condition.type) {
		case 'all_of':
		case 'any_of':
		case 'at_least_of': {
			return condition.conditions.some((nested) => conditionNamesItem(nested, itemId));
		}
		case 'has_item': {
			return condition.itemId === itemId;
		}
		case 'not': {
			return conditionNamesItem(condition.condition, itemId);
		}
		default: {
			return false;
		}
	}
};
